'use client';

import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import {
  Tooltip,
  TooltipContent,
  type TooltipContentProps,
  TooltipProvider,
  TooltipTrigger,
} from '@vs-shop/ui/tooltip';
import { clsxm } from '@vs-shop/utils';

import { productStockLevelClasses, type ProductStockLevelProps } from './productStock.tsx';

/**
 * The stock block behind a tooltip: a compact placement states availability in
 * one line and keeps the full block for a shopper who asks for it.
 *
 *   <ProductStockTooltip>
 *     <ProductStockTooltipTrigger variant={ProductStockLevel.AVAILABLE}>
 *       Skladem > 10 ks
 *     </ProductStockTooltipTrigger>
 *     <ProductStockTooltipContent>
 *       <ProductStock variant={ProductStockVariant.PLAIN}>…</ProductStock>
 *     </ProductStockTooltipContent>
 *   </ProductStockTooltip>
 *
 * The client leaf of the section, and the only one: the parts in
 * `productStock.tsx` are plain server components, and they reach the popup as
 * `children` rather than by being imported here — a client module rendering a
 * server sibling would turn that sibling into a client component too.
 */

/**
 * The tooltip.
 *
 * The delay is the section's rather than a caller's: this affordance sits on a
 * line of copy inside a card, where a shopper's pointer crosses it on the way
 * somewhere else, and a tooltip that opened instantly would flicker across a
 * listing grid.
 */
export type ProductStockTooltipProps = ComponentProps<typeof Tooltip>;

/** The tooltip around a compact stock line. */
export const ProductStockTooltip = (props: ProductStockTooltipProps) => (
  <TooltipProvider delay={200}>
    <Tooltip {...props} />
  </TooltipProvider>
);

/**
 * The line a shopper reads without asking, and the surface that opens the rest.
 *
 * It swallows the pointer rather than letting it through, because the compact
 * placements put this line INSIDE a whole-surface link: a shopper reaching for
 * the availability would otherwise land on the product page. Both the click and
 * the press that precedes it are stopped — a link fires on either.
 */
export const productStockTooltipTriggerVariants = cva('', {
  variants: { variant: productStockLevelClasses },
});

export type ProductStockTooltipTriggerProps = Omit<
  ComponentProps<typeof TooltipTrigger>,
  'render'
> &
  ProductStockLevelProps;

/**
 * The compact availability line that opens the block.
 *
 * A caller's own `onClick` / `onMouseDown` run first and the swallowing follows
 * them, rather than the caller's replacing it: the guarantee is the part's, so
 * spreading the caller's props over these two handlers would let any call site
 * silently give it up.
 */
export const ProductStockTooltipTrigger = ({
  className,
  variant,
  onClick,
  onMouseDown,
  ...props
}: ProductStockTooltipTriggerProps) => (
  <TooltipTrigger
    render={
      <span
        data-slot="product-stock-tooltip-trigger"
        className={clsxm(productStockTooltipTriggerVariants({ variant }), className)}
      />
    }
    {...props}
    onClick={(event) => {
      onClick?.(event);
      event.preventDefault();
      event.stopPropagation();
    }}
    onMouseDown={(event) => {
      onMouseDown?.(event);
      event.preventDefault();
      event.stopPropagation();
    }}
  />
);

/**
 * The popup.
 *
 * Anchored to the trigger's far edge and pulled back inside it, so a line
 * sitting at the right of a narrow card opens a popup that stays on the card
 * rather than off the side of the grid. Those are the section's judgements
 * about where a stock popup belongs, so they are not caller props.
 */
export type ProductStockTooltipContentProps = Omit<TooltipContentProps, 'align' | 'alignOffset'>;

/** The block the trigger reveals. */
export const ProductStockTooltipContent = ({
  className,
  ...props
}: ProductStockTooltipContentProps) => (
  <TooltipContent
    data-slot="product-stock-tooltip-content"
    align="end"
    alignOffset={-10}
    className={clsxm('w-full', className)}
    {...props}
  />
);
